import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import unitStatusApi from '../services/unitStatusApi';
import StopUnitModal from './StopUnitModal';
import { formatDate, formatShortDateTime } from '../utils/dateFormatter';
import {
  UNIT_STATUS_LABELS,
  UNIT_STATUS_REASONS,
  UNIT_STATUS_PRESUMED,
  UNIT_STATUS_PRESUMED_HINT,
  UNIT_STATUS_NOTES,
  STOP_REASON_LABELS,
  STOP_CHANNEL_LABELS,
  UNIT_STOP_UI as UI,
  stoppedBy,
} from '../config/unitStatusLabels';
import { UNIT_STOP_ERROR_CODES } from '../utils/unitStopErrorCodes';
import '../styles/UnitStatusPanel.css';

/**
 * «حالة التشغيل» on the unit page — tasks/unit-operational-status.md S3.
 *
 * Replaces the stored-field badge and its dropdown. The status is WORKED OUT by the backend on every
 * read (D1); this component only shows it and offers the one ops action, a stop (D13) — nobody sets
 * «جاهزة» by hand any more, they fix the record behind it.
 *
 * One strip, the loudest line in the header, because it is the one fact about the unit that changes
 * during the day: the status colours its inline-start edge, the reason reads beside it, notes sit
 * quietly underneath. Owners get the simplified word only (§9 item 6) — the backend shapes that, so
 * nothing internal ever reaches their browser.
 */
const STATUS_TONE = {
  OCCUPIED: 'occupied',
  STOPPED: 'stopped',
  AWAITING_CLEANING: 'waiting',
  UNKNOWN: 'unknown',
  READY: 'ready',
  PREPARING: 'waiting',
  AVAILABLE: 'ready',
};

const UnitStatusPanel = ({ propertyId, canManage, onChanged }) => {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [modal, setModal] = useState(null); // 'stop' | 'edit' | 'resume' | null
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState(null);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const res = await unitStatusApi.get(propertyId);
      setData(res.unitStatus);
      setLoadError(null);
    } catch (err) {
      console.error('Failed to load unit status:', err);
      // Our own sentence, never the server's: a refusal from the ownership check arrives in English.
      setLoadError(UI.loadFailed);
    }
  }, [propertyId]);

  // A different unit starts empty: the previous unit's status (and its «استئناف» button, which would
  // act on the previous unit's stop) must never be on screen while the new one loads.
  useEffect(() => { setData(null); setLoadError(null); setModal(null); load(); }, [load]);

  // The channel send runs in the background — while it is still «جارٍ…», look again shortly so the
  // strip settles on confirmed/failed without a reload (walkthrough F1). Bounded: at most a few tries.
  const pending = data?.stop?.channelStatus === 'pending';
  const [pendingTries, setPendingTries] = useState(0);
  useEffect(() => {
    if (!pending || pendingTries >= 5) return undefined;
    const timer = setTimeout(() => { setPendingTries((n) => n + 1); load(); }, 4000);
    return () => clearTimeout(timer);
  }, [pending, pendingTries, load]);

  const resume = async () => {
    setResuming(true);
    setResumeError(null);
    try {
      await unitStatusApi.resume(data.stop.stopId);
      setModal(null);
      load();
      onChanged?.();
    } catch (err) {
      // Someone else resumed it (another tab or person): the unit is not stopped any more — show the
      // current truth instead of a stale «موقوفة» beside a refusal (walkthrough F3).
      if (err.response?.data?.code === UNIT_STOP_ERROR_CODES.ALREADY_RESOLVED) {
        setModal(null);
        load();
        onChanged?.();
        return;
      }
      setResumeError(err.response?.data?.error || UI.saveFailed);
    } finally {
      setResuming(false);
    }
  };

  if (loadError) {
    return (
      <div className="unit-status-strip tone-unknown" role="status">
        <span className="unit-status-reason">{loadError}</span>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="unit-status-strip tone-loading" aria-busy="true">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  // Staff only: owners see the owner pages (their status words live in ownerApp.unitStatus), never this strip.
  const label = UNIT_STATUS_LABELS[data.status];
  const tone = STATUS_TONE[data.status] || 'unknown';
  const stop = data.stop || null;
  const notes = data.notes || [];
  const awaitingTicket = data.status === 'AWAITING_CLEANING' ? data.cleaning?.ticketId || null : null;

  return (
    <section className={`unit-status-strip tone-${tone}`} aria-label={UI.title}>
      <div className="unit-status-main">
        <span className="unit-status-title">{UI.title}</span>
        <span className="unit-status-pill">{label || data.status}</span>

        <span className="unit-status-reason">
          {stop?.reasonCode ? `${STOP_REASON_LABELS[stop.reasonCode] || stop.reasonCode} — ` : ''}
          {/* Who stopped it, when known (walkthrough F4); otherwise the generic reason. */}
          {data.reasonCode === 'UNIT_STOPPED' && stop?.createdByName
            ? stoppedBy(stop.createdByName)
            : (UNIT_STATUS_REASONS[data.reasonCode] || '')}
          {data.confidence === 'PRESUMED' && <span className="unit-status-presumed" title={UNIT_STATUS_PRESUMED_HINT}>{UNIT_STATUS_PRESUMED}</span>}
        </span>

        {canManage && (
          <div className="unit-status-actions">
            {stop ? (
              <>
                <Button size="sm" variant="outline-secondary" onClick={() => setModal('edit')}>{UI.edit}</Button>
                <Button size="sm" variant="success" onClick={() => { setResumeError(null); setModal('resume'); }}>{UI.resume}</Button>
              </>
            ) : (
              <Button size="sm" variant="outline-danger" onClick={() => setModal('stop')}>
                <i className="bi bi-slash-circle" aria-hidden="true"></i> {UI.stop}
              </Button>
            )}
          </div>
        )}
      </div>

      {(stop || notes.length > 0 || data.cleaning?.late || data.cleaning?.atRisk || awaitingTicket) && (
        <ul className="unit-status-notes">
          {/* The open clean, by ticket — so ops know which job to chase (walkthrough F6). */}
          {awaitingTicket && (
            <li>{UNIT_STATUS_NOTES.cleaningTask} <span className="unit-status-ticket" dir="ltr">{awaitingTicket}</span></li>
          )}
          {stop && (
            <li>
              {stop.note && <span className="unit-status-stop-note">«{stop.note}»</span>}
              {stop.createdAt && <span>{UI.stoppedSince} {formatShortDateTime(stop.createdAt)}</span>}
              {stop.reviewAt && <span>{UI.reviewOn} {formatShortDateTime(stop.reviewAt)}</span>}
              {stop.closedThrough && <span>{UI.closedUntil} {formatDate(stop.closedThrough)}</span>}
            </li>
          )}
          {stop && STOP_CHANNEL_LABELS[stop.channelStatus] && (
            <li className={stop.channelStatus === 'failed' ? 'is-alarm' : ''}>{STOP_CHANNEL_LABELS[stop.channelStatus]}</li>
          )}
          {data.cleaning?.late && <li className="is-alarm">{UNIT_STATUS_NOTES.cleaningLate}</li>}
          {!data.cleaning?.late && data.cleaning?.atRisk && <li className="is-alarm">{UNIT_STATUS_NOTES.cleaningAtRisk}</li>}
          {notes.map((n) => (
            <li key={n.code}>
              {n.code === 'OPEN_MAINTENANCE' ? (
                <>
                  {UNIT_STATUS_NOTES.openMaintenance(n.count)}
                  {(n.tickets || []).map((t) => (
                    <span key={t.taskId} className={t.fromLastClean ? 'unit-status-ticket is-fresh' : 'unit-status-ticket'} dir="ltr">
                      {t.ticketId || '—'}
                    </span>
                  ))}
                  {(n.tickets || []).some((t) => t.fromLastClean) && <span className="unit-status-fresh">{UNIT_STATUS_NOTES.fromLastClean}</span>}
                </>
              ) : (
                UNIT_STATUS_NOTES[n.code] || n.code
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <StopUnitModal
          show={modal === 'stop' || modal === 'edit'}
          onHide={() => setModal(null)}
          propertyId={propertyId}
          stop={modal === 'edit' ? stop : null}
          businessDate={data.businessDate}
          onSaved={() => { setPendingTries(0); load(); onChanged?.(); }}
        />
      )}

      {canManage && stop && (
        <Modal show={modal === 'resume'} onHide={resuming ? undefined : () => setModal(null)} centered dir="rtl">
          <Modal.Header closeButton={!resuming}>
            <Modal.Title>{UI.resumeModalTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {resumeError && <Alert variant="danger" className="py-2">{resumeError}</Alert>}
            <p className="mb-0">{UI.resumeBody}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setModal(null)} disabled={resuming}>{UI.cancel}</Button>
            <Button variant="success" onClick={resume} disabled={resuming}>
              {resuming ? <Spinner animation="border" size="sm" /> : UI.resumeConfirm}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </section>
  );
};

export default UnitStatusPanel;
